import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import * as XLSX from 'xlsx';

interface ExpenseManagementProps {
  domain: string;
}

interface Expense {
  id: string;
  category: string;
  subCategory: string;
  description: string;
  amount: number;
  paymentMode: string;
  transactionId: string;
  vendor: string;
  date: string;
  month: string;
  year: string;
  approvedBy: string;
  status: 'pending' | 'approved' | 'rejected';
  remarks: string;
  createdAt: string;
  createdBy: string;
  attachmentUrl?: string;
}

// Expense categories (defined outside component for better performance)
const EXPENSE_CATEGORIES: Record<string, string[]> = {
  'Infrastructure': ['Building Maintenance', 'Furniture', 'Equipment', 'Renovation', 'Other'],
  'Utilities': ['Electricity', 'Water', 'Internet', 'Phone', 'Other'],
  'Salaries & Benefits': ['Teacher Salaries', 'Staff Salaries', 'Allowances', 'Bonuses', 'Other'],
  'Educational': ['Books', 'Stationery', 'Lab Equipment', 'Sports Equipment', 'Library', 'Other'],
  'Administrative': ['Office Supplies', 'Printing', 'Software', 'Licenses', 'Insurance', 'Other'],
  'Transportation': ['Fuel', 'Bus Maintenance', 'Vehicle Insurance', 'Driver Salaries', 'Other'],
  'Events & Activities': ['Cultural Events', 'Sports Events', 'Annual Day', 'Excursions', 'Other'],
  'Marketing': ['Advertisements', 'Brochures', 'Website', 'Social Media', 'Other'],
  'Miscellaneous': ['Taxes', 'Legal', 'Consultancy', 'Donations', 'Other']
};

const ExpenseManagement: React.FC<ExpenseManagementProps> = ({ domain }) => {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeView, setActiveView] = useState<'add' | 'list' | 'reports'>('list');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterMonth, setFilterMonth] = useState('all');
  const [filterYear, setFilterYear] = useState(new Date().getFullYear().toString());
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => (currentYear - 2 + i).toString());

  const [expenseForm, setExpenseForm] = useState<Omit<Expense, 'id' | 'createdAt'>>({
    category: '',
    subCategory: '',
    description: '',
    amount: 0,
    paymentMode: 'Bank Transfer',
    transactionId: '',
    vendor: '',
    date: new Date().toISOString().split('T')[0],
    month: new Date().toLocaleString('default', { month: 'long' }),
    year: new Date().getFullYear().toString(),
    approvedBy: '',
    status: 'pending',
    remarks: '',
    createdBy: '',
  });

  useEffect(() => {
    loadExpenses();
  }, [domain]);

  const loadExpenses = async () => {
    setLoading(true);
    try {
      const expensesRef = collection(db, `school-data/${domain}/expenses`);
      const snapshot = await getDocs(expensesRef);

      const expensesList: Expense[] = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      } as Expense));

      setExpenses(expensesList.sort((a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ));
    } catch (error: any) {
      console.error('Error loading expenses:', error);
      alert(`Failed to load expenses: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (field: keyof Omit<Expense, 'id' | 'createdAt'>, value: any) => {
    setExpenseForm({ ...expenseForm, [field]: value });
  };

  const handleAddExpense = async () => {
    if (!expenseForm.category || !expenseForm.description || expenseForm.amount <= 0) {
      alert('Please fill in all required fields!');
      return;
    }

    setLoading(true);
    try {
      const userDataString = localStorage.getItem('userData');
      const currentUser = userDataString ? JSON.parse(userDataString) : null;
      const currentUserEmail = currentUser?.email || 'admin';

      const expenseId = `exp_${Date.now()}`;
      const expenseData: Expense = {
        ...expenseForm,
        id: expenseId,
        createdAt: new Date().toISOString(),
        createdBy: currentUserEmail,
      };

      await setDoc(doc(db, `school-data/${domain}/expenses`, expenseId), expenseData);

      alert('Expense added successfully!');
      resetForm();
      loadExpenses();
      setActiveView('list');
    } catch (error: any) {
      console.error('Error adding expense:', error);
      alert(`Failed to add expense: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateExpense = async () => {
    if (!editingExpense) return;

    setLoading(true);
    try {
      const expenseRef = doc(db, `school-data/${domain}/expenses`, editingExpense.id);
      await updateDoc(expenseRef, {
        ...expenseForm,
      });

      alert('Expense updated successfully!');
      setEditingExpense(null);
      resetForm();
      loadExpenses();
      setActiveView('list');
    } catch (error: any) {
      console.error('Error updating expense:', error);
      alert(`Failed to update expense: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteExpense = async (expenseId: string) => {
    if (!confirm('Are you sure you want to delete this expense?')) return;

    setLoading(true);
    try {
      await deleteDoc(doc(db, `school-data/${domain}/expenses`, expenseId));
      alert('Expense deleted successfully!');
      loadExpenses();
    } catch (error: any) {
      console.error('Error deleting expense:', error);
      alert(`Failed to delete expense: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (expense: Expense) => {
    if (!confirm(`Approve this expense of ₹${expense.amount.toLocaleString('en-IN')}?`)) return;

    setLoading(true);
    try {
      const userDataString = localStorage.getItem('userData');
      const currentUser = userDataString ? JSON.parse(userDataString) : null;
      const currentUserEmail = currentUser?.email || 'admin';

      const expenseRef = doc(db, `school-data/${domain}/expenses`, expense.id);
      await updateDoc(expenseRef, {
        status: 'approved',
        approvedBy: currentUserEmail,
      });

      alert('Expense approved successfully!');
      loadExpenses();
    } catch (error: any) {
      console.error('Error approving expense:', error);
      alert(`Failed to approve expense: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async (expense: Expense) => {
    const reason = prompt('Please enter reason for rejection:');
    if (!reason) return;

    setLoading(true);
    try {
      const expenseRef = doc(db, `school-data/${domain}/expenses`, expense.id);
      await updateDoc(expenseRef, {
        status: 'rejected',
        remarks: reason,
      });

      alert('Expense rejected successfully!');
      loadExpenses();
    } catch (error: any) {
      console.error('Error rejecting expense:', error);
      alert(`Failed to reject expense: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (expense: Expense) => {
    setEditingExpense(expense);
    setExpenseForm({
      category: expense.category,
      subCategory: expense.subCategory,
      description: expense.description,
      amount: expense.amount,
      paymentMode: expense.paymentMode,
      transactionId: expense.transactionId,
      vendor: expense.vendor,
      date: expense.date,
      month: expense.month,
      year: expense.year,
      approvedBy: expense.approvedBy,
      status: expense.status,
      remarks: expense.remarks,
      createdBy: expense.createdBy,
    });
    setActiveView('add');
  };

  const resetForm = () => {
    setExpenseForm({
      category: '',
      subCategory: '',
      description: '',
      amount: 0,
      paymentMode: 'Bank Transfer',
      transactionId: '',
      vendor: '',
      date: new Date().toISOString().split('T')[0],
      month: new Date().toLocaleString('default', { month: 'long' }),
      year: new Date().getFullYear().toString(),
      approvedBy: '',
      status: 'pending',
      remarks: '',
      createdBy: '',
    });
    setEditingExpense(null);
  };

  const filteredExpenses = expenses.filter(expense => {
    if (filterCategory !== 'all' && expense.category !== filterCategory) return false;
    if (filterStatus !== 'all' && expense.status !== filterStatus) return false;
    if (filterMonth !== 'all' && expense.month !== filterMonth) return false;
    if (filterYear !== 'all' && expense.year !== filterYear) return false;
    return true;
  });

  const calculateTotals = () => {
    const total = filteredExpenses.reduce((sum, exp) => sum + exp.amount, 0);
    const approved = filteredExpenses.filter(e => e.status === 'approved').reduce((sum, exp) => sum + exp.amount, 0);
    const pending = filteredExpenses.filter(e => e.status === 'pending').reduce((sum, exp) => sum + exp.amount, 0);
    const rejected = filteredExpenses.filter(e => e.status === 'rejected').reduce((sum, exp) => sum + exp.amount, 0);

    return { total, approved, pending, rejected };
  };

  const downloadReport = () => {
    const reportData = filteredExpenses.map(exp => ({
      'Date': exp.date,
      'Category': exp.category,
      'Sub-Category': exp.subCategory,
      'Description': exp.description,
      'Amount': exp.amount,
      'Vendor': exp.vendor,
      'Payment Mode': exp.paymentMode,
      'Transaction ID': exp.transactionId,
      'Status': exp.status.toUpperCase(),
      'Approved By': exp.approvedBy || 'N/A',
      'Remarks': exp.remarks || '',
      'Created By': exp.createdBy,
    }));

    const ws = XLSX.utils.json_to_sheet(reportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Expenses');

    const fileName = `expenses_${filterMonth !== 'all' ? filterMonth : 'all'}_${filterYear}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const totals = calculateTotals();

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-white py-3">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h5 className="mb-0">
              <i className="bi bi-wallet2 me-2 text-primary-custom"></i>
              Expense Management
            </h5>
            <small className="text-muted">Track and manage school expenses</small>
          </div>
          <div className="btn-group">
            <button
              className={`btn btn-sm ${activeView === 'list' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => { setActiveView('list'); resetForm(); }}
            >
              <i className="bi bi-list-ul me-1"></i>
              List
            </button>
            <button
              className={`btn btn-sm ${activeView === 'add' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setActiveView('add')}
            >
              <i className="bi bi-plus-circle me-1"></i>
              Add Expense
            </button>
            <button
              className={`btn btn-sm ${activeView === 'reports' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setActiveView('reports')}
            >
              <i className="bi bi-graph-up me-1"></i>
              Reports
            </button>
          </div>
        </div>
      </div>

      <div className="card-body">
        {loading && (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 text-muted">Loading expenses...</p>
          </div>
        )}

        {!loading && activeView === 'add' && (
          <div>
            <h6 className="mb-3">
              {editingExpense ? 'Edit Expense' : 'Add New Expense'}
            </h6>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Category *</label>
                <select
                  className="form-select"
                  value={expenseForm.category}
                  onChange={(e) => {
                    const selectedCategory = e.target.value;
                    setExpenseForm({
                      ...expenseForm,
                      category: selectedCategory,
                      subCategory: ''
                    });
                  }}
                >
                  <option value="">Select Category</option>
                  {Object.keys(EXPENSE_CATEGORIES).map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div className="col-md-6">
                <label className="form-label">Sub-Category *</label>
                <select
                  className="form-select"
                  value={expenseForm.subCategory}
                  onChange={(e) => {
                    setExpenseForm({
                      ...expenseForm,
                      subCategory: e.target.value
                    });
                  }}
                  disabled={!expenseForm.category}
                >
                  <option value="">Select Sub-Category</option>
                  {expenseForm.category && EXPENSE_CATEGORIES[expenseForm.category] ? (
                    EXPENSE_CATEGORIES[expenseForm.category].map(sub => (
                      <option key={sub} value={sub}>{sub}</option>
                    ))
                  ) : null}
                </select>
                {expenseForm.category && (
                  <small className="text-success mt-1 d-block">
                    ✓ Category selected: {expenseForm.category}
                  </small>
                )}
              </div>

              <div className="col-md-12">
                <label className="form-label">Description *</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={expenseForm.description}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                  placeholder="Enter expense description"
                />
              </div>

              <div className="col-md-4">
                <label className="form-label">Amount (₹) *</label>
                <input
                  type="number"
                  className="form-control"
                  value={expenseForm.amount}
                  onChange={(e) => handleFormChange('amount', parseFloat(e.target.value) || 0)}
                  min="0"
                  step="0.01"
                />
              </div>

              <div className="col-md-4">
                <label className="form-label">Vendor/Payee</label>
                <input
                  type="text"
                  className="form-control"
                  value={expenseForm.vendor}
                  onChange={(e) => handleFormChange('vendor', e.target.value)}
                  placeholder="Vendor name"
                />
              </div>

              <div className="col-md-4">
                <label className="form-label">Date *</label>
                <input
                  type="date"
                  className="form-control"
                  value={expenseForm.date}
                  onChange={(e) => handleFormChange('date', e.target.value)}
                />
              </div>

              <div className="col-md-4">
                <label className="form-label">Payment Mode *</label>
                <select
                  className="form-select"
                  value={expenseForm.paymentMode}
                  onChange={(e) => handleFormChange('paymentMode', e.target.value)}
                >
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Cash">Cash</option>
                  <option value="Cheque">Cheque</option>
                  <option value="UPI">UPI</option>
                  <option value="Credit Card">Credit Card</option>
                  <option value="Debit Card">Debit Card</option>
                </select>
              </div>

              <div className="col-md-4">
                <label className="form-label">Transaction ID / Reference</label>
                <input
                  type="text"
                  className="form-control"
                  value={expenseForm.transactionId}
                  onChange={(e) => handleFormChange('transactionId', e.target.value)}
                  placeholder="Transaction reference"
                />
              </div>

              <div className="col-md-4">
                <label className="form-label">Status</label>
                <select
                  className="form-select"
                  value={expenseForm.status}
                  onChange={(e) => handleFormChange('status', e.target.value as 'pending' | 'approved' | 'rejected')}
                >
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>

              <div className="col-md-12">
                <label className="form-label">Remarks</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={expenseForm.remarks}
                  onChange={(e) => handleFormChange('remarks', e.target.value)}
                  placeholder="Additional notes"
                />
              </div>

              <div className="col-md-12">
                <button
                  className="btn btn-primary me-2"
                  onClick={editingExpense ? handleUpdateExpense : handleAddExpense}
                  disabled={loading}
                >
                  {loading ? 'Processing...' : (editingExpense ? 'Update Expense' : 'Add Expense')}
                </button>
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => { setActiveView('list'); resetForm(); }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {!loading && activeView === 'list' && (
          <div>
            {/* Filters */}
            <div className="row g-3 mb-4">
              <div className="col-md-3">
                <label className="form-label small">Category</label>
                <select
                  className="form-select form-select-sm"
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                >
                  <option value="all">All Categories</option>
                  {Object.keys(EXPENSE_CATEGORIES).map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div className="col-md-2">
                <label className="form-label small">Status</label>
                <select
                  className="form-select form-select-sm"
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>

              <div className="col-md-3">
                <label className="form-label small">Month</label>
                <select
                  className="form-select form-select-sm"
                  value={filterMonth}
                  onChange={(e) => setFilterMonth(e.target.value)}
                >
                  <option value="all">All Months</option>
                  {months.map(month => (
                    <option key={month} value={month}>{month}</option>
                  ))}
                </select>
              </div>

              <div className="col-md-2">
                <label className="form-label small">Year</label>
                <select
                  className="form-select form-select-sm"
                  value={filterYear}
                  onChange={(e) => setFilterYear(e.target.value)}
                >
                  {years.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>

              <div className="col-md-2 d-flex align-items-end">
                <button
                  className="btn btn-success btn-sm w-100"
                  onClick={downloadReport}
                >
                  <i className="bi bi-download me-1"></i>
                  Export
                </button>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="row g-3 mb-4">
              <div className="col-md-3">
                <div className="card bg-light">
                  <div className="card-body">
                    <h6 className="text-muted mb-1 small">Total Expenses</h6>
                    <h4 className="mb-0">₹{totals.total.toLocaleString('en-IN')}</h4>
                    <small className="text-muted">{filteredExpenses.length} entries</small>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card bg-success bg-opacity-10">
                  <div className="card-body">
                    <h6 className="text-success mb-1 small">Approved</h6>
                    <h4 className="mb-0 text-success">₹{totals.approved.toLocaleString('en-IN')}</h4>
                    <small className="text-muted">{filteredExpenses.filter(e => e.status === 'approved').length} entries</small>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card bg-warning bg-opacity-10">
                  <div className="card-body">
                    <h6 className="text-warning mb-1 small">Pending</h6>
                    <h4 className="mb-0 text-warning">₹{totals.pending.toLocaleString('en-IN')}</h4>
                    <small className="text-muted">{filteredExpenses.filter(e => e.status === 'pending').length} entries</small>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card bg-danger bg-opacity-10">
                  <div className="card-body">
                    <h6 className="text-danger mb-1 small">Rejected</h6>
                    <h4 className="mb-0 text-danger">₹{totals.rejected.toLocaleString('en-IN')}</h4>
                    <small className="text-muted">{filteredExpenses.filter(e => e.status === 'rejected').length} entries</small>
                  </div>
                </div>
              </div>
            </div>

            {/* Expenses Table */}
            {filteredExpenses.length === 0 ? (
              <div className="text-center py-5">
                <i className="bi bi-inbox display-1 text-muted"></i>
                <p className="mt-3 text-muted">No expenses found.</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead className="table-light">
                    <tr>
                      <th>Date</th>
                      <th>Category</th>
                      <th>Description</th>
                      <th>Amount</th>
                      <th>Vendor</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredExpenses.map((expense) => (
                      <tr key={expense.id}>
                        <td>
                          <small>{new Date(expense.date).toLocaleDateString('en-IN')}</small>
                        </td>
                        <td>
                          <strong>{expense.category}</strong>
                          <br />
                          <small className="text-muted">{expense.subCategory}</small>
                        </td>
                        <td>
                          {expense.description}
                          <br />
                          <small className="text-muted">
                            {expense.paymentMode} {expense.transactionId && `- ${expense.transactionId}`}
                          </small>
                        </td>
                        <td>
                          <strong>₹{expense.amount.toLocaleString('en-IN')}</strong>
                        </td>
                        <td>
                          <small>{expense.vendor || 'N/A'}</small>
                        </td>
                        <td>
                          {expense.status === 'approved' && (
                            <span className="badge bg-success">
                              <i className="bi bi-check-circle me-1"></i>
                              Approved
                            </span>
                          )}
                          {expense.status === 'pending' && (
                            <span className="badge bg-warning">
                              <i className="bi bi-clock me-1"></i>
                              Pending
                            </span>
                          )}
                          {expense.status === 'rejected' && (
                            <span className="badge bg-danger">
                              <i className="bi bi-x-circle me-1"></i>
                              Rejected
                            </span>
                          )}
                        </td>
                        <td>
                          <div className="btn-group btn-group-sm">
                            <button
                              className="btn btn-outline-primary"
                              onClick={() => handleEdit(expense)}
                              title="Edit"
                            >
                              <i className="bi bi-pencil"></i>
                            </button>
                            {expense.status === 'pending' && (
                              <>
                                <button
                                  className="btn btn-outline-success"
                                  onClick={() => handleApprove(expense)}
                                  title="Approve"
                                >
                                  <i className="bi bi-check-lg"></i>
                                </button>
                                <button
                                  className="btn btn-outline-danger"
                                  onClick={() => handleReject(expense)}
                                  title="Reject"
                                >
                                  <i className="bi bi-x-lg"></i>
                                </button>
                              </>
                            )}
                            <button
                              className="btn btn-outline-danger"
                              onClick={() => handleDeleteExpense(expense.id)}
                              title="Delete"
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {!loading && activeView === 'reports' && (
          <div>
            <h6 className="mb-3">Expense Reports & Analytics</h6>
            <div className="row g-4">
              <div className="col-md-12">
                <div className="card">
                  <div className="card-header bg-light">
                    <h6 className="mb-0">Category-wise Breakdown</h6>
                  </div>
                  <div className="card-body">
                    <div className="table-responsive">
                      <table className="table table-sm">
                        <thead>
                          <tr>
                            <th>Category</th>
                            <th className="text-end">Total Amount</th>
                            <th className="text-end">Count</th>
                            <th className="text-end">Percentage</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.keys(EXPENSE_CATEGORIES).map(cat => {
                            const catExpenses = filteredExpenses.filter(e => e.category === cat);
                            const catTotal = catExpenses.reduce((sum, e) => sum + e.amount, 0);
                            const percentage = totals.total > 0 ? ((catTotal / totals.total) * 100).toFixed(1) : '0.0';

                            if (catExpenses.length === 0) return null;

                            return (
                              <tr key={cat}>
                                <td><strong>{cat}</strong></td>
                                <td className="text-end">₹{catTotal.toLocaleString('en-IN')}</td>
                                <td className="text-end">{catExpenses.length}</td>
                                <td className="text-end">
                                  <div className="progress" style={{ height: '20px', minWidth: '80px' }}>
                                    <div
                                      className="progress-bar"
                                      role="progressbar"
                                      style={{ width: `${percentage}%` }}
                                      aria-valuenow={parseFloat(percentage)}
                                      aria-valuemin={0}
                                      aria-valuemax={100}
                                    >
                                      {percentage}%
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot className="table-light">
                          <tr>
                            <th>Total</th>
                            <th className="text-end">₹{totals.total.toLocaleString('en-IN')}</th>
                            <th className="text-end">{filteredExpenses.length}</th>
                            <th className="text-end">100%</th>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default ExpenseManagement;
